import axios from 'axios';
// import { serialize } from 'object-to-formdata';

import { API_PATHS, ADMIN_API_PATHS } from "../components/Config";

const SF1_TOKEN = 'sf1_token';
const OPTIONS = {
    ACTIONS: 'actions'
};

const apiClient = axios.create({
    timeout: 8000,
    withCredentials: true,
});

const getAuthToken = () => {
    return localStorage.getItem(SF1_TOKEN);
};

const getAuthTokenPayload = () => {
    const token = getAuthToken();
    if (!token) { return false; }
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
        console.log(error);
        return false;
    }
};

const setAuthToken =(token) => {
    if (token) {
        localStorage.setItem(SF1_TOKEN, token)
        console.log("Setting instance Authorization header:", token);
        apiClient.defaults.headers.common['Authorization'] = `token ${token}`;
    } else {
        localStorage.removeItem(SF1_TOKEN)
        delete apiClient.defaults.headers.common['Authorization'];
    }
};


/** Alias/BrowseAs Version 0.1
 * TODO: Refactor this when we remove PHP
 * 
 * Append alias=<ATTENDEE_ID> parameter to specific API URLs
 */
const appendAlias = () => {
    if (window.JS_ALIAS_ID) {
        return `?alias=${window.JS_ALIAS_ID}`;
    }
    return '';
}


/** Peek into the global vars set by PHP */
const getMandatedAttendeeId = () => {
    return window.JS_MANDATE_ATTENDEE_ID ?? 0;
};
const isAnonymous = () => {
    return (window.JS_MANDATE_ATTENDEE_ID === 0); // EXACT EQUALS
};


/* Number of seconds remaining till the token expires.
 * 
 * @return {Number} seconds. -1 if invalid for some reason.
 */
const authTokenRemainingSeconds = () => {
    const payload = getAuthTokenPayload();
    if (!payload) {
        return -1;
    }

    // Full validation happens on the server.
    // We just need to know that the attendee is correct and token hasn't expired, so that we can refresh if necessary
    // ...so we can inspect the payload manually rather than use a full jwt library
    //
    // To check the attendee, we need the V1 frontend to inject a JS_MANDATE_ATTENDEE_ID field.
    try {
        if (window.JS_MANDATE_ATTENDEE_ID) {
            // console.log(payload, window.JS_MANDATE_ATTENDEE_ID);
            if (payload.sub != window.JS_MANDATE_ATTENDEE_ID) {
                console.log("MANDATE_ATTENDEE_MISMATCH!");
                return -1;
            }
        }

        if (! {}.hasOwnProperty.call(payload, 'manager')) {
            console.log("Old JWT format");
            return -1; // Must be an old token from before the manager was added
        }
        const nowSeconds = Math.floor( Date.now() / 1000 );
        return payload.exp - nowSeconds;

    } catch (error) {
        console.log('hasValidAuthToken: Error:', error);
        return -1;
    }
};


const hasValidAuthToken = () => {
    let seconds = authTokenRemainingSeconds();
    return seconds > 0;
};

const isManager = () => {
    const payload = getAuthTokenPayload();
    if (!payload) { return false; }
    return payload.manager;
};

const jwtPayloadName = () => {
    const payload = getAuthTokenPayload();
    if (!payload) { return "Auth error"; }
    return payload.name;
};

const jwtPayloadAttendeeId = () => {
    const payload = getAuthTokenPayload();
    if (!payload) { return "Auth error"; }
    return Number(payload.sub);
};

/* If the user has a valid sf1 session, the cookies on this request will allow us to retrieve a JWT
 * that can be used for the Django API calls.
 */
const requestAuthToken = (useCache=true) => {
    if (useCache && hasValidAuthToken()) {
        setAuthToken(getAuthToken());
        return Promise.resolve('Success');
    } else {
        // Check EITHER the frontend or backend auth, dependent on window.location.pathname
        if (window.location.pathname.includes('/admin/')) {
            console.log("Admin auth validation...");
            return apiClient.get(API_PATHS.AUTH_TOKEN_ADMIN).then(response => {
                setAuthToken(response.data['jwt']);
            });
        } else {
            console.log("Frontend auth validation...");
            return apiClient.get(API_PATHS.AUTH_TOKEN).then(response => {
                setAuthToken(response.data['jwt']);
            });
        }
    }
};


/** Retrieve the bare profile for the current user
 */
const getHTMLProfileBlob = () => {
    return apiClient.get(API_PATHS.HTML_PROFILE);
};


const EXPIRE_MS = 1000 * 60 * 10; // 10mins in milliseconds

// const checkCache = (path) => {
//     let cache = sessionStorage.getItem(path);
//     if (cache) {
//         let cacheObj = JSON.parse(cache);
//         // cacheObj should contain timestamp and data
//         let expire_at = Date.now() - EXPIRE_MS;
//         if (expire_at < cacheObj.time) {
//             return new Promise(() => {
//                 return cacheObj;
//             });
//         }
//     }
//     return null;
// };

const getCache = (path) => {
    let cache = sessionStorage.getItem(path);
    if (cache) {
        try {
            let cacheObj = JSON.parse(cache);
            // cacheObj should contain timestamp and data
            let expire_at = Date.now() - EXPIRE_MS;
            if (expire_at < cacheObj.time) {
                return cacheObj;
            } else {
                sessionStorage.removeItem(path);
            }
        } catch (err)  {
            console.error(err);
            sessionStorage.removeItem(path);
        }
    }
    return null;
};
const setCache = (path, axiosPromise) => {
    return axiosPromise.then(response => {
        sessionStorage.setItem(path, JSON.stringify({
            time: Date.now(),
            data: response.data,
        }));
        return response;
    });
};

const cacheWrapper = async (path, axiosObj, doCache) => {
    console.log('cacheWrapper:', doCache);
    if (doCache) {
        let cacheObj = getCache(path);
        if (cacheObj) {
            cacheObj.isCached = true;  // Add a hint that we used a cached value
            return new Promise((resolve) => { resolve(cacheObj); });
        }
    }
    return setCache(path, axiosObj(path));
}



export default {
    getCache,
    setCache,
    cacheWrapper,
    isAnonymous,
    getMandatedAttendeeId,
    setAuthToken,
    authTokenRemainingSeconds,
    hasValidAuthToken,
    requestAuthToken,
    hasAuthToken() {
        return !!localStorage.getItem(SF1_TOKEN); // JS equivalent of ternary '? true : false'
    },
    isManager,
    jwtPayloadName,
    jwtPayloadAttendeeId,
    getHTMLProfileBlob,
    /*
    Retrieve all the basic event, meeting_slot, settings, user data as a single object:
    {
        event: ...
        settings: ...
        urls: ...
        TODO: + slots?
        attendee: ...
        organisation: ...
        group: ...
        notes: ...
    }
     */
    getMe(cache) {
        console.log("getMe:", cache);
        var path = API_PATHS.ME + appendAlias();
        return apiClient.get(path);
        // return cacheWrapper(path, apiClient.get, cache);
        // if (cache) {

        // }
        // return apiClient.get(path);
        // var response = checkCache(path)
        // if (response) { return response; }
        // return setCache(path, apiClient.get(path));
    },

    // getEvent() {
    //     return apiClient.get(API_PATHS.EVENT);
    // },

    getAttendees() {
        return apiClient.get(API_PATHS.ATTENDEES);
    },

    getOrganisations() {
        return apiClient.get(API_PATHS.ORGANISATIONS);
    },

    getLatestMessages() {
        return apiClient.get(API_PATHS.MESSAGES_LATEST);
    },

    getMyMessagePointers() {
        return apiClient.get(API_PATHS.MESSAGES_MY_POINTERS);
    },

    getMeetings() {
        return apiClient.get(API_PATHS.MEETINGS + appendAlias());
    },

    getAdminMeetingsForSlot(slot_id) {
        return apiClient.get(API_PATHS.ADMIN_MEETINGS+'?slot_id='+slot_id);
    },

    getSlots() {
    // getSlots(cache) {
        // console.log("getSlots:", cache);
        var path = API_PATHS.SLOTS;
        return apiClient.get(path);
        // return cacheWrapper(path, apiClient.get, cache);
    },

    getTracks() {
        return apiClient.get(API_PATHS.TRACKS);
    },

    getAppointments() {  // OBSOLETE! - Replaced by getMyAppointments
        return apiClient.get(API_PATHS.APPOINTMENTS);
    },
    getMyAppointments() {
        return apiClient.get(API_PATHS.MY_APPOINTMENTS + appendAlias());
    },

    getCustomFields() {
        return apiClient.get(API_PATHS.CUSTOM_FIELDS);
    },

    getGroups() {
        return apiClient.get(API_PATHS.GROUPS);
    },

    getLiveVideos() {
        return apiClient.get(API_PATHS.VIDEOS_LIVE);
    },

    /* IMPOSSIBLE IN AXIOS - WE CAN'T PREVENT REDIRECTS IN THE BROWSER
    async getRedirectURL(url) {
        console.log('getRedirectURL');
        return apiClient.get(url + appendAlias(), {
            maxRedirects: 0,
        })
            .then(function(response) {
                console.log(response);
                console.log(response.headers);
                return response.headers['Location'];
            });
    },
    */
    async getSchedulePrintRedirect() {
        console.log('getSchedulePrintRedirect');
        return apiClient.get(API_PATHS.SCHEDULE_PRINT_REDIRECT + appendAlias())
            .then(({data}) => {
                return data.location
            });
    },

    login(username, password) {
        return apiClient.post(API_PATHS.AUTH, {},{
            auth: {
                username: username,
                password: password
            }
        });
    },
    async logout() {
        console.log("EventService: Starting logout");
        await apiClient.get(API_PATHS.LOGOUT);
        console.log("EventService: After call");
        this.setAuthToken('');


        console.log("EventService. Wiping cookies");
        window.$cookies.keys().forEach(cookie => window.$cookies.remove(cookie));

        console.log("EventService. Finished");
        return true;
    },

    /** POST a message
     *
     * {
     *  message: "message body",
     *  attendee_id: 1234,
     *  meeting_id: 1234
     * }
     *
     * Either attendee_id Or meeting_id ~ sender_id comes from logged-in user
     *
     * @param data
     * @returns {Promise<AxiosResponse<T>>}
     */
    sendMessage(data) {
        return apiClient.post(API_PATHS.MESSAGES, data)
    },

    markMessageAsRead(msg) {
        return apiClient.post(API_PATHS.MESSAGES_READ_BASE + msg.id);
    },

    addNote(note) {
        return apiClient.post(API_PATHS.NOTES, note);
    },
    removeNote(note) {
        return apiClient.delete(API_PATHS.NOTES + note.id);
    },

    getOptions(path) {
        return apiClient.options(path);
    },

    /** Return the POST/PUT schemas returned by the OPTIONS call
     *
     * TODO: Throw 'offline' exception if not available?
     * */
    getFormSchema(path) {
        return this.getOptions(path)
            .then(({data}) => {
                console.log("getFormSchema");
                console.log(data);
                return data[OPTIONS.ACTIONS];
            });
    },

    getFormData(path) {
        return apiClient.get(path);
    },
    putFormData(path, data) {
        return apiClient.put(path, data);
    },
    postFormData(path, data) {
        return apiClient.post(path, data);
    },

    // Multipart forms used for Attendee/Organisation EditForms where file uploads may be required.
    // Do we need to use object-to-formdata.serialize() to handle nested form elements like register[attendee]?
    // Consider using Object.assign({}, formElement) or JSON.parse(JSON.stringify(formElement)) to remove Vue magic?
    putMultipartData(path, formElement) {
        const formData = new FormData(formElement);
        // const formData = serialize(formElement);
        return apiClient.put(path, formData);
    },
    postMultipartData(path, formElement) {
        const formData = new FormData(formElement);
        // const formData = serialize(stripped);
        return apiClient.post(path, formData);
    },

    // MEETING / CALENDAR
    postPrivate(slotId) {
        return apiClient.post(API_PATHS.MEETING_PRIVATE + appendAlias(), {slot_id: slotId});
    },
    postAvailable(slotId) {
        return apiClient.post(API_PATHS.MEETING_AVAILABLE + appendAlias(), {slot_id: slotId});
    },

    // VIDEO
    postVideoHook(sessionId, connectionId, hook) {
        return apiClient.post(
            API_PATHS.VIDEO_HOOK,
            {
                session_id: sessionId,
                connection_id: connectionId,
                hook: hook,
            }
        );
    },
    startRecording(sessionId) {
        return apiClient.post(
            API_PATHS.VIDEO_ARCHIVE_START,
            {session_id: sessionId}
        )
    },
    stopRecording(sessionId) {
        return apiClient.post(
            API_PATHS.VIDEO_ARCHIVE_STOP,
            {session_id: sessionId}
        )
    },

    getVideoArchivesForAppointment(appointmentId) {
        return apiClient.get(
            API_PATHS.VIDEO_ARCHIVE_APPOINTMENT + appointmentId
        )
    },
    deleteVideoArchive(videoArchiveId) {
        return apiClient.delete(
            API_PATHS.VIDEO_ARCHIVE + videoArchiveId + '/'
        )
    },

    // Jitsi video
    getJitsiToken(params) {
        if (params.meeting_id) {
            return this.getJitsiTokenForMeeting(params.meeting_id);
        } else if (params.appointment_id) {
            return this.getJitsiTokenForAppointment(params.appointment_id);
        } else if (params.attendee_id) {
            return this.getJitsiTokenForAttendee(params.attendee_id);
        }
        throw "Jitsi video token missing parameter";
    },
    getJitsiTokenForMeeting(id) {
        return apiClient.get(
            API_PATHS.VIDEO_JITSI_TOKEN+'?meeting_id='+id
        );
    },
    getJitsiTokenForAppointment(id) {
        return apiClient.get(
            API_PATHS.VIDEO_JITSI_TOKEN+'?appointment_id='+id
        );
    },
    getJitsiTokenForAttendee(id) {
        return apiClient.get(
            API_PATHS.VIDEO_JITSI_TOKEN+'?attendee_id='+id
        );
    },
    postJitsiBrowserHook(eventName, payload) {
        return apiClient.post(API_PATHS.VIDEO_JITSI_BROWSERHOOK, {
            eventName: eventName,
            payload: payload
        });
    },

    /* Build url paths for [appointment|attendee|meeting] chat websocket */
    urlForAppointmentChatSocket(id) {
        return API_PATHS.CHAT_BASE + `appointment/${id}/${getAuthToken()}/`;
    },
    urlForAttendeeChatSocket(id) {
        return API_PATHS.CHAT_BASE + `attendee/${id}/${getAuthToken()}/`;
    },
    urlForMeetingChatSocket(id) {
        return API_PATHS.CHAT_BASE + `meeting/${id}/${getAuthToken()}/`;
    },
    /* V3 Single path for websocket, using room-routing for messages */
    urlForChatSocket() {
        return API_PATHS.CHAT_BASE + getAuthToken() + `/`;
    },
    urlForLatestMessagesChatSocket() {
        return API_PATHS.CHAT_BASE + `latest-messages/${getAuthToken()}/`;
    },

    // Simple name-based attendee search
    getAttendeeQuickSearch(query) {
        return apiClient.get(API_PATHS.QUICKSEARCH + `?q=${query}`);
    },

    getLatestMessageForRoom(room) {
        return apiClient.get(API_PATHS.MESSAGES_ROOM_INFO + room);
    },

    // ADMIN API CALLS
    getAdminAppointment(id) {
        console.log('getAdminAppointment...');
        return apiClient.get(ADMIN_API_PATHS.APPOINTMENTS+id+'/');
    },
    adminAppointmentAttendeeAdd(id, attendeeId) {
        console.log('adminAppointmentAdd...');
        return apiClient.post(`${ADMIN_API_PATHS.APPOINTMENTS}${id}/add/${attendeeId}`);
    },
    adminAppointmentAttendeeRemove(id, attendeeId) {
        console.log('adminAppointmentRemove...');
        return apiClient.post(`${ADMIN_API_PATHS.APPOINTMENTS}${id}/remove/${attendeeId}`);
    },
    adminAppointmentAttendeeConfirmed(id, attendeeId, value) {
        console.log('adminAppointmentConfirmed...');
        return apiClient.post(`${ADMIN_API_PATHS.APPOINTMENTS}${id}/confirmed/${attendeeId}`, {is_confirmed: value});
    },
    adminAppointmentAttendeeModerator(id, attendeeId, value) {
        console.log('adminAppointmentModerator...');
        return apiClient.post(`${ADMIN_API_PATHS.APPOINTMENTS}${id}/moderator/${attendeeId}`, {moderator: value});
    },
    adminCustomFieldGroupToggle(field_id, group_id, value) {
        return apiClient.post(`${ADMIN_API_PATHS.CUSTOMFIELD_EDIT}${field_id}/toggle_group/`, {
            group: group_id,
            active: value
        });
    },
    adminCustomFieldReOrder(field_id, index) {
        return apiClient.post(`${ADMIN_API_PATHS.CUSTOMFIELD_EDIT}${field_id}/reorder/`, {
            order_weight: index
        });
    },
    adminMeetingVerified(meeting_id, state) {
        return apiClient.post(`${ADMIN_API_PATHS.MEETING_VERIFIED}${meeting_id}/`, {
            state: state
        });
    },
    
    adminGroupReports() {
        return apiClient.get(ADMIN_API_PATHS.GROUP_REPORTS);
    },
    adminGroupReportsBuild(groupId, force=false) {
        let param = force ? '?force=1' : '';
        return apiClient.post(`${ADMIN_API_PATHS.GROUP_REPORTS_BUILD}${groupId}/${param}`);
    },

    getAdminAttendeeSearch(query) {
        return apiClient.get(ADMIN_API_PATHS.SEARCH + `?q=${query}`);
    },

    getSuperAdminEventList() {
        return apiClient.get('/api/v2/superadmin/event_list');
    },
    postSuperAdminCloneEvent(data) {
        // apiClient config sets the timeout to 8000ms
        // Cloning may take longer, so we need to adjust to 30000 (30 seconds).
        return apiClient.post('/api/v2/superadmin/clone-event/', data, {timeout: 30000});
    },
};
